// Body-proportion ratios with sex-specific reference targets, computed from logged
// circumferences. Two widely-cited ratios — shown as rough guides (sources vary), the same
// framing as lib/standards.js. Stored values are in cm but ratios are unit-agnostic.
//
// Sources:
//  • Waist-to-hip ratio (health) — WHO cut-offs for elevated cardiometabolic risk:
//    men ≥ 0.90, women ≥ 0.85. The pear-shaped (low-WHR) pattern is the lower-risk one.
//    Traditional aesthetic ideal: women ~0.70, men ~0.85–0.90.
//  • Shoulder-to-waist ratio (aesthetic "V-taper") — the golden ratio φ ≈ 1.618; shoulders
//    ~1.6× the waist is the classic athletic male silhouette. Less emphasised for women,
//    where waist-to-hip is the primary shape signal — shown as a softer "balance" guide.

// Bucketed result → { value, label, status, hint }. status drives the colour pill
// ("good" = success, "ok" = accent/warn, "watch" = danger/neutral).
function band(value, bands, hint) {
  for (const b of bands) if (value <= b.max) return { value, label: b.label, status: b.status, hint };
  const last = bands[bands.length - 1];
  return { value, label: last.label, status: last.status, hint };
}

/** Waist ÷ hips. Lower is leaner-waisted. */
export function waistHipRatio(waistCm, hipCm, sex) {
  if (!waistCm || !hipCm) return null;
  const v = waistCm / hipCm;
  if (sex === "female") {
    return band(v, [
      { max: 0.80, label: "Healthy", status: "good" },
      { max: 0.85, label: "Moderate", status: "ok" },
      { max: Infinity, label: "Elevated risk", status: "watch" },
    ], "Aim under 0.85 (WHO) · classic ideal ~0.70");
  }
  return band(v, [
    { max: 0.90, label: "Healthy", status: "good" },
    { max: 0.95, label: "Moderate", status: "ok" },
    { max: Infinity, label: "Elevated risk", status: "watch" },
  ], "Aim under 0.90 (WHO)");
}

/** Shoulders ÷ waist. Higher is a stronger V-taper. */
export function shoulderWaistRatio(shoulderCm, waistCm, sex) {
  if (!shoulderCm || !waistCm) return null;
  const v = shoulderCm / waistCm;
  // Higher-is-better, so the bands read top-down: invert by bucketing on a descending scale.
  const grade = (good, athletic) => {
    if (v >= good) return { value: v, label: "Golden V-taper", status: "good" };
    if (v >= athletic) return { value: v, label: "Athletic", status: "ok" };
    return { value: v, label: "Developing", status: "watch" };
  };
  if (sex === "female") {
    return { ...grade(1.4, 1.2), hint: "Balance guide · ~1.4 reads athletic" };
  }
  return { ...grade(1.6, 1.4), hint: "Golden ratio ~1.618 (φ)" };
}

/** Waist ÷ height. Key indicator of cardiometabolic health across sexes.
 * Source: Ashwell & Gibson (2014) / NICE guidelines (2022).
 * Rule of thumb: "Keep your waist circumference to less than half your height" (WHtR < 0.50).
 */
export function waistHeightRatio(waistCm, heightCm) {
  if (!waistCm || !heightCm) return null;
  const v = waistCm / heightCm;
  return band(v, [
    { max: 0.39, label: "Low", status: "watch" },
    { max: 0.49, label: "Healthy (<0.50)", status: "good" },
    { max: 0.59, label: "Increased risk", status: "ok" },
    { max: Infinity, label: "High risk", status: "watch" },
  ], "Aim under 0.50 · keep waist under half your height (NICE)");
}

/**
 * Build the list of computable ratios from a {site: current_cm} map for the given sex.
 * Optionally includes waist-to-height ratio if heightCm is provided.
 * Returns [{ key, name, ...result }] for whichever inputs are present.
 */
export function proportions(currentBySite, sex, heightCm = null) {
  const out = [];
  const whr = waistHipRatio(currentBySite.waist, currentBySite.hips, sex);
  if (whr) out.push({ key: "whr", name: "Waist-to-hip", ...whr });
  if (heightCm) {
    const wtr = waistHeightRatio(currentBySite.waist, heightCm);
    if (wtr) out.push({ key: "wtr", name: "Waist-to-height", ...wtr });
  }
  const sw = shoulderWaistRatio(currentBySite.shoulders, currentBySite.waist, sex);
  if (sw) out.push({ key: "sw", name: "Shoulder-to-waist", ...sw });
  return out;
}
